import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ReceiptIcon from '@material-ui/icons/Receipt';
import GavelIcon from '@material-ui/icons/Gavel';
import DescriptionIcon from '@material-ui/icons/Description';
import TimelineIcon from '@material-ui/icons/Timeline';
import WorkIcon from '@material-ui/icons/Work';
import { tokens } from '../../theme';
import { HRIS_SIGN_IN, useOpenJobCount } from '../Jobs/hrisJobs';
import { useHasNewPublication } from '../Publications/newPublications';
import useSubdomainUrl from '../../subdomains/useSubdomainUrl';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        backgroundColor: tokens.blue900,
        backgroundImage: `linear-gradient(112deg, ${tokens.blue900} 0%, ${tokens.blue700} 58%, ${tokens.blue600} 100%)`,
        color: '#ffffff',
        overflow: 'hidden',
        borderBottom: `4px solid ${tokens.gold}`,
    },
    pattern: {
        position: 'absolute',
        inset: 0,
        opacity: 0.16,
        backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
        backgroundSize: '24px 24px',
        pointerEvents: 'none',
    },
    inner: {
        position: 'relative',
        paddingTop: theme.spacing(9),
        paddingBottom: theme.spacing(9),
        [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(6),
            paddingBottom: theme.spacing(6),
        },
    },
    eyebrow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        color: tokens.goldSoft,
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        marginBottom: theme.spacing(2.5),
    },
    eyebrowRule: {
        display: 'inline-block',
        width: 34,
        height: 2,
        backgroundColor: tokens.gold,
    },
    title: {
        color: '#ffffff',
        marginBottom: theme.spacing(3),
        maxWidth: '18ch',
        [theme.breakpoints.down('sm')]: { fontSize: '2rem', maxWidth: 'none' },
    },
    lede: {
        color: 'rgba(255,255,255,0.88)',
        fontSize: '1.125rem',
        lineHeight: 1.7,
        maxWidth: '54ch',
        margin: `0 0 ${theme.spacing(4)}px`,
        [theme.breakpoints.down('sm')]: { fontSize: '1rem' },
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    ctaPrimary: {
        backgroundColor: tokens.white,
        color: tokens.blue900,
        padding: '12px 24px',
        '&:hover': { backgroundColor: tokens.goldSoft },
    },
    ctaSecondary: {
        color: '#ffffff',
        borderColor: 'rgba(255,255,255,0.55)',
        padding: '12px 24px',
        '&:hover': {
            borderColor: '#ffffff',
            backgroundColor: 'rgba(255,255,255,0.08)',
        },
    },

    /* Popular services panel */
    panel: {
        backgroundColor: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: tokens.radius,
        padding: theme.spacing(3),
        backdropFilter: 'blur(2px)',
    },
    panelTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: tokens.goldSoft,
        margin: `0 0 ${theme.spacing(2)}px`,
        paddingBottom: theme.spacing(1.5),
        borderBottom: '1px solid rgba(255,255,255,0.18)',
    },
    panelList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
    },
    panelItem: {
        borderBottom: '1px solid rgba(255,255,255,0.12)',
        '&:last-child': { borderBottom: 'none' },
    },
    panelLink: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.75, 0.5),
        color: '#ffffff',
        textDecoration: 'none',
        fontSize: '0.9375rem',
        fontWeight: 500,
        transition: 'padding-left 150ms ease, color 150ms ease',
        '&:hover': {
            color: tokens.goldSoft,
            paddingLeft: theme.spacing(1.5),
        },
        '&:hover $panelArrow': { opacity: 1 },
    },
    panelIcon: {
        color: tokens.goldSoft,
        display: 'flex',
        fontSize: 20,
    },
    panelLabel: { flexGrow: 1 },
    panelArrow: {
        fontSize: 16,
        opacity: 0.5,
        transition: 'opacity 150ms ease',
    },
    hiringBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        borderRadius: 999,
        backgroundColor: tokens.gold,
        color: tokens.blue900,
        fontSize: '0.75rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    hiringDot: {
        position: 'relative',
        width: 7,
        height: 7,
        borderRadius: '50%',
        backgroundColor: tokens.green,
        '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: tokens.green,
            animation: '$hiringPulse 1.8s ease-out infinite',
        },
        '@media (prefers-reduced-motion: reduce)': {
            '&::after': { animation: 'none' },
        },
    },
    '@keyframes hiringPulse': {
        '0%': { transform: 'scale(1)', opacity: 0.7 },
        '100%': { transform: 'scale(2.6)', opacity: 0 },
    },
    srOnly: {
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
    },
}));

const popularServices = [
    {
        label: 'Vendor details & invoice tracking',
        icon: <ReceiptIcon fontSize="inherit" />,
        href: 'https://public-facing-vendortracking.mof.gov.tl/login',
        // Fixed call to action; remove this line to hide the badge.
        badge: 'Update now',
    },
    {
        label: 'Find open tender opportunities',
        icon: <GavelIcon fontSize="inherit" />,
        to: '/tenders',
    },
    {
        label: 'Budget documents and reports',
        icon: <DescriptionIcon fontSize="inherit" />,
        to: '/publications',
        newDocuments: true,
    },
    {
        label: 'Budget and revenue by year',
        icon: <TimelineIcon fontSize="inherit" />,
        to: '/budgetandrevenueallyears',
    },
    {
        label: 'e-Procurement Portal',
        icon: <OpenInNewIcon fontSize="inherit" />,
        href: 'http://www.eprocurement.gov.tl/',
    },
    {
        label: 'e-Recruitment Portal',
        icon: <WorkIcon fontSize="inherit" />,
        href: HRIS_SIGN_IN,
        hiring: true,
    },
];

/** Homepage hero: institutional statement plus the portal's most-used tasks. */
export default function HeroBanner() {
    const classes = useStyles();
    const siteUrl = useSubdomainUrl();
    const openJobs = useOpenJobCount();
    const hasNewPublication = useHasNewPublication();
    const hiringLabel = openJobs === 1 ? '1 open vacancy' : `${openJobs} open vacancies`;

    return (
        <section className={classes.root} aria-labelledby="hero-title">
            <div className={classes.pattern} aria-hidden="true" />
            <Container maxWidth="lg">
                <div className={classes.inner}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <span className={classes.eyebrow}>
                                <span className={classes.eyebrowRule} aria-hidden="true" />
                                Ministry of Finance
                            </span>

                            <Typography
                                id="hero-title"
                                variant="h1"
                                component="h1"
                                className={classes.title}
                            >
                                Managing the nation's public finances
                            </Typography>

                            <p className={classes.lede}>
                                We plan and execute the State Budget, collect public revenue and
                                report on how it is spent — so that citizens, businesses and
                                partners can see the state of Timor-Leste's finances.
                            </p>

                            <div className={classes.actions}>
                                {/* The budget directorate's own site, as in Quick Services. */}
                                <Button
                                    href={siteUrl('budget')}
                                    variant="contained"
                                    className={classes.ctaPrimary}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    Budget and spending
                                </Button>
                                <Button
                                    component={RouterLink}
                                    to="/publications"
                                    variant="outlined"
                                    className={classes.ctaSecondary}
                                >
                                    Publications and reports
                                </Button>
                            </div>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <div className={classes.panel}>
                                <p className={classes.panelTitle}>Popular services</p>
                                <ul className={classes.panelList}>
                                    {popularServices.map((service) => (
                                        <li key={service.label} className={classes.panelItem}>
                                            {service.href ? (
                                                <a
                                                    className={classes.panelLink}
                                                    href={service.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <span className={classes.panelIcon}>{service.icon}</span>
                                                    <span className={classes.panelLabel}>{service.label}</span>
                                                    {service.badge && (
                                                        <span className={classes.hiringBadge}>
                                                            <span className={classes.hiringDot} aria-hidden="true" />
                                                            {service.badge}
                                                        </span>
                                                    )}
                                                    {service.hiring && openJobs > 0 && (
                                                        <span className={classes.hiringBadge} title={hiringLabel}>
                                                            <span className={classes.hiringDot} aria-hidden="true" />
                                                            {`We're hiring · ${openJobs}`}
                                                            <span className={classes.srOnly}>{`, ${hiringLabel}`}</span>
                                                        </span>
                                                    )}
                                                    <ArrowForwardIcon className={classes.panelArrow} />
                                                </a>
                                            ) : (
                                                <RouterLink className={classes.panelLink} to={service.to}>
                                                    <span className={classes.panelIcon}>{service.icon}</span>
                                                    <span className={classes.panelLabel}>{service.label}</span>
                                                    {service.newDocuments && hasNewPublication && (
                                                        <span className={classes.hiringBadge}>
                                                            <span className={classes.hiringDot} aria-hidden="true" />
                                                            New document uploaded
                                                        </span>
                                                    )}
                                                    <ArrowForwardIcon className={classes.panelArrow} />
                                                </RouterLink>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </Container>
        </section>
    );
}
